import { LitElement, html, nothing } from "lit";
import { customElement, property, query, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";

import "@phosphor-icons/webcomponents/PhPause";
import "@phosphor-icons/webcomponents/PhPlay";
import "@phosphor-icons/webcomponents/PhStop";
import "@phosphor-icons/webcomponents/PhWarning";
import "ui-components/button/wow-button";
import "ui-components/modal/wow-modal";
import "ui-components/stream-recorder/stream-recorder";

import type { StreamRecorder } from "ui-components/stream-recorder/stream-recorder";

import { styles } from "./live-stream-modal.css";

type AspectRatio = "landscape" | "portrait";

@customElement("live-stream-modal")
export class LiveStreamModal extends LitElement {
  static override styles = [styles];

  @property({ type: String })
  streamId!: string;

  @property({ type: String, attribute: "aspect-ratio" })
  aspectRatio: AspectRatio = "landscape";

  @property({ type: Boolean, attribute: "enable-audio" })
  enableAudio = true;

  @query("stream-recorder")
  private streamRecorder?: StreamRecorder;

  @state()
  private isStreaming = false;

  @state()
  private isPaused = false;

  @state()
  private isConnecting = false;

  @state()
  private streamingDuration = 0;

  @state()
  private errorMessage = "";

  private durationInterval: number | null = null;

  override connectedCallback() {
    super.connectedCallback();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.stopDurationTimer();
  }

  override firstUpdated() {
    void this.startStream();
  }

  private async startStream(): Promise<void> {
    if (!this.streamRecorder) return;

    this.isConnecting = true;
    this.errorMessage = "";

    try {
      await this.streamRecorder.startStreaming();
    } catch (error) {
      this.errorMessage = error instanceof Error ? error.message : "Failed to start streaming";
      this.isConnecting = false;
    }
  }

  private handleStreamingStarted(): void {
    this.isStreaming = true;
    this.isConnecting = false;
    this.startDurationTimer();
  }

  private handleStreamingStopped(): void {
    this.isStreaming = false;
    this.isPaused = false;
    this.stopDurationTimer();
    this.dispatchEvent(new CustomEvent("stream-ended", { bubbles: true, composed: true }));
  }

  private handleStreamingError(e: CustomEvent): void {
    this.errorMessage = e.detail?.message || "Streaming error occurred";
    this.isConnecting = false;
    this.isStreaming = false;
  }

  private handleStreamingPaused(): void {
    this.isPaused = true;
  }

  private handleStreamingResumed(): void {
    this.isPaused = false;
  }

  private startDurationTimer(): void {
    this.streamingDuration = 0;
    this.durationInterval = window.setInterval(() => {
      this.streamingDuration++;
    }, 1000);
  }

  private stopDurationTimer(): void {
    if (this.durationInterval !== null) {
      clearInterval(this.durationInterval);
      this.durationInterval = null;
    }
  }

  private formatDuration(seconds: number): string {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
    }
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }

  private handlePauseResume(): void {
    if (!this.streamRecorder) return;

    if (this.isPaused) {
      this.streamRecorder.resumeStreaming();
    } else {
      this.streamRecorder.pauseStreaming();
    }
  }

  private handleStop(): void {
    if (this.streamRecorder) {
      void this.streamRecorder.stopStreaming();
    }
  }

  private handleClose(): void {
    this.handleStop();
  }

  override render() {
    const liveBadgeClasses = {
      "live-badge": true,
      paused: this.isPaused,
    };

    return html`
      <wow-modal prevent-close mobile-fullscreen @close=${this.handleClose}>
        <span slot="header">
          ${this.isStreaming
            ? html`
                <div class="streaming-header">
                  <div class=${classMap(liveBadgeClasses)}>
                    <div class="live-indicator"></div>
                    <span>${this.isPaused ? "Paused" : "Live"}</span>
                  </div>
                  <span class="duration">${this.formatDuration(this.streamingDuration)}</span>
                </div>
              `
            : this.isConnecting
              ? "Connecting..."
              : "Stream"}
        </span>

        <div class="streaming-content">
          ${this.errorMessage
            ? html`
                <div class="error-banner">
                  <ph-warning size="20"></ph-warning>
                  <span>${this.errorMessage}</span>
                </div>
              `
            : nothing}
          ${this.isConnecting && !this.isStreaming
            ? html`
                <div class="connecting-state">
                  <div class="spinner"></div>
                  <span>Starting stream...</span>
                </div>
              `
            : nothing}

          <div class="video-container">
            <stream-recorder
              .streamId=${this.streamId}
              aspect-ratio=${this.aspectRatio}
              ?enable-audio=${this.enableAudio}
              show-preview
              hide-controls
              @streaming-started=${this.handleStreamingStarted}
              @streaming-stopped=${this.handleStreamingStopped}
              @streaming-error=${this.handleStreamingError}
              @streaming-paused=${this.handleStreamingPaused}
              @streaming-resumed=${this.handleStreamingResumed}
            ></stream-recorder>
          </div>

          ${this.isStreaming
            ? html`
                <div class="streaming-controls">
                  <wow-button variant="neutral" size="medium" @click=${this.handlePauseResume}>
                    ${this.isPaused
                      ? html`<ph-play size="18" weight="fill"></ph-play> Resume`
                      : html`<ph-pause size="18" weight="fill"></ph-pause> Pause`}
                  </wow-button>
                  <wow-button variant="danger" size="medium" @click=${this.handleStop}>
                    <ph-stop size="18" weight="fill"></ph-stop>
                    Stop streaming
                  </wow-button>
                </div>
              `
            : nothing}
        </div>
      </wow-modal>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "live-stream-modal": LiveStreamModal;
  }
}
